import React, { FC, useMemo } from 'react'
import { CouponIcon } from '@/atoms/Icons/CouponIcon'
import { Popover } from '@/atoms/Popover'
import { CaptionMD, EyebrowMD, TitleXS } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPromotionType } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'

interface DiscountPillProps {
  promoType: TheatricalPromotionType
  discount: number
}

export const DiscountPill: FC<DiscountPillProps> = ({ promoType, discount }) => {
  const { t } = useTranslate('tickets')

  const discountInfo = useMemo(() => {
    if (!discount) return null

    if (promoType === 'NO_FEES') {
      return {
        title: t('noFeesTicketsOnSelectTimes', 'No Fees on Select Times'),
        description: t('noFeesPopoverDescription', 'No service fees on select showtimes at Angel Partner Theaters.'),
      }
    }

    if (promoType === 'EARLY_BIRD') {
      return {
        title: t('couponDiscountSelectTimesV2', '{{discount}}% off Select Times', {
          discount,
        }),
        description: (
          <>
            <TitleXS color="white" weight="semibold" className={'pt-2'}>
              {t('specificShowTimesDetails', 'Specific Showtimes Details')}
            </TitleXS>
            <div className="mt-2 flex flex-col gap-0.5 text-[0.688rem] uppercase leading-[16px] text-white">
              <EyebrowMD>
                {t('monWedThurs', 'Mon, Wed & Thurs')} <span className="uppercase">({t('allDay', 'All Day')})</span>
              </EyebrowMD>
              <EyebrowMD>{t('friBefore5AndAfter9', 'Fri (Before 5pm & After 9pm)')}</EyebrowMD>
              <EyebrowMD>{t('satToSunAfter9', 'Sat-Sun (After 9pm)')}</EyebrowMD>
            </div>
          </>
        ),
      }
    }

    return null
  }, [discount, promoType, t])

  if (!discountInfo) return null

  return (
    <Popover
      className="!z-10"
      title={t('discountPopoverTitle', 'Available Discounts')}
      description={discountInfo.description}
      icon={<CouponIcon color="warm-03" size={20} />}
      triggerComponent={
        <div className="relative flex items-center justify-center gap-1 rounded-full border border-core-warning-200 bg-core-warning-50 px-3 py-0.5">
          <CouponIcon color="core-warning-800" size={14} />
          <CaptionMD color="core-warning-800">{discountInfo.title}</CaptionMD>
        </div>
      }
    />
  )
}
